import type { Identifier } from '@medplum/fhirtypes';

/** Liza deployment stage. Mirrors the runtime values of `Secrets['LIZA_STAGE']`. */
export type LizaStage = 'local' | 'staging' | 'production';

/** FHIR identifier system URLs for Liza resources in Medplum. */
export const IDENTIFIER_SYSTEMS = {
  AUTH0_ORG_ID: 'https://liza.health/fhir/identifier/auth0-org-id',
  AUTH0_USER_ID: 'https://liza.health/fhir/identifier/auth0-user-id',
  /**
   * External bundle id: persists the upstream resource's original FHIR `id` when importing NDJSON
   * (Fasten, Metriport export, scripts, etc.) so Medplum can upsert idempotently. The URL segment `fasten-original-id`
   * is historical only; treat this as the canonical external-source id system everywhere.
   */
  EXTERNAL_ORIGINAL_ID: 'https://liza.health/fhir/identifier/fasten-original-id',
  LIZA_ID: 'https://liza.health/fhir/identifier/liza-id',
  /**
   * Hint patient id (`object.id` on a `patient.*` webhook event). The stable key the Hint webhook
   * worker resolves incoming events by, so an email or birthdate correction in Hint updates the
   * existing record instead of creating a second one.
   */
  HINT_PATIENT_ID: 'https://liza.health/fhir/identifier/hint-patient-id',
  OKTA_USER_ID: 'https://liza.health/fhir/identifier/okta-user-id',
  PARTNER_MRN: 'https://liza.health/fhir/identifier/partner-mrn',
  ONBOARDING_VERSION: 'https://liza.health/fhir/identifier/onboarding-version',
  /** Metriport facility ID for this org (patient bot reads it from `Patient.managingOrganization`). */
  METRIPORT_ORGANIZATION_ID: 'https://metriport.com/fhir/identifiers/organization-id',
  /** Metriport patient ID — used for Medplum `Patient` identifiers. */
  METRIPORT_PATIENT_IDENTIFIER_SYSTEM: 'https://metriport.com/fhir/identifiers/patient-id',
  /** US National Provider Identifier (10-digit CMS-issued ID). FHIR-canonical system URL. */
  NPI: 'http://hl7.org/fhir/sid/us-npi',
  /** Health Gorilla's identifier system — used for HG-side org/lab/practitioner/patient ids. */
  HEALTH_GORILLA: 'https://www.healthgorilla.com',
  /**
   * Identifier system URL for Liza-owned `Device` resources — the service principals Liza's own
   * automated writes are attributed to. See {@link DEVICE_IDENTIFIERS} for the well-known values.
   */
  DEVICE: 'https://liza.health/fhir/identifier/device',
  /**
   * Identifier system URL for `Task` occurrences materialized from a recurring template.
   * The value is `${templateId}:${date}:${slotCode ?? ''}` — a stable key for the
   * (template, date, slot) tuple. Used with `If-None-Exist` to make slot completion
   * race-free: concurrent requests for the same slot end up with one Task, not two.
   */
  TASK_OCCURRENCE_SLOT: 'https://liza.health/fhir/identifier/task-occurrence-slot',
  /**
   * Identifier system for a survey `QuestionnaireResponse` submitted against a Task. The value
   * mirrors the occurrence it belongs to — `${templateId}:${date}:${slotCode ?? ''}` for a slot,
   * or `oneoff:${taskId}` for a one-off. Used with `If-None-Exist` so the multi-step submit
   * (materialize occurrence → create QR → complete Task) is retry-safe and never duplicates the QR.
   */
  QUESTIONNAIRE_RESPONSE_SLOT: 'https://liza.health/fhir/identifier/questionnaire-response-slot',
  /**
   * Identifier system for an `Observation` synced from a device health store (Apple HealthKit /
   * Android Health Connect). The value is platform-namespaced — `healthkit:<sample-uuid>` /
   * `healthconnect:<record-id>` (see {@link buildHealthSampleIdentifier}). The platform prefix
   * doubles as device-sync provenance. Used with a conditional `PUT` so a repeated sync of the
   * same device sample upserts one Observation rather than duplicating, and an in-place edit
   * (Health Connect) overwrites the stored value. Scope is same-device, repeated-sync only — these
   * source ids are device-local and do not dedupe across devices.
   */
  HEALTH_SAMPLE: 'https://liza.health/fhir/identifier/health-sample',
  /**
   * Idempotency key for an outbound-fax recipient `Organization` (see
   * https://github.com/Liza-Health/app/issues/6449 for the model). The value is
   * `${sendingOrganizationId}:${normalizedFax}` — a stable key for the
   * (sending org, fax) tuple. Used with `If-None-Exist` so concurrent creates of the
   * same recipient end up with one Organization, not two.
   */
  OUTBOUND_FAX_RECIPIENT_KEY: 'https://liza.health/fhir/identifier/outbound-fax-recipient-key',
  /**
   * eFax user id. The value is the Organization's eFax account id; Medplum's
   * `$receive-efax` operation sources it from this identifier to poll that account for new faxes.
   * Its presence marks a resource as eFax-enabled.
   * https://www.medplum.com/docs/integration/efax
   */
  EFAX: 'https://efax.com',
  /**
   * Stamped on the per-org service `Bot` that backs `medplumClientForOrganization`. The value is
   * the Organization's Medplum id — the medplum-config deploy (`deployOrgServiceBot`) converges
   * the principal on it, and the runtime resolves (never creates) by it. The Bot carries no
   * source code and never executes — it exists only as the impersonation target whose
   * `ProjectMembership` scopes background work to exactly one org.
   */
  ORG_SERVICE_IDENTITY: 'https://liza.health/fhir/identifier/org-service-identity',
  /**
   * Cal.com team id for a practice `Organization`. The value is the numeric team id (stored as a
   * string) whose members were that practice's bookable clinicians. Nothing reads it: scheduling
   * resolves a practice's bookable clinicians from their `Schedule` resources. Kept because existing
   * `Organization`s carry it, and removing it is a data migration rather than a code change.
   */
  CALCOM_TEAM_ID: 'https://liza.health/fhir/identifier/calcom-team-id',
  /**
   * Cal.com username for a clinician `Practitioner`, still editable on the practitioner form. Nothing
   * in the scheduling path reads it any more — booking, availability and the schedule all resolve a
   * clinician through their `Schedule` resources — so it is now a record of the clinician's cal.com
   * handle rather than a join key.
   */
  CALCOM_USERNAME: 'https://liza.health/fhir/identifier/calcom-username',
  /**
   * Cal.com booking uid on an `Appointment` that cal.com wrote, back when it owned bookings. Nothing
   * writes it now; it survives on historical resources, which is why reads must treat a visit without
   * one as ordinary rather than as a special case.
   */
  CALCOM_BOOKING_UID: 'https://liza.health/fhir/identifier/calcom-booking-uid',
  /**
   * Marks a resource as synthetic (persona-generated test data — see the test-fixture-api synthetic
   * loader). The value namespaces the synthetic resource within its patient, e.g.
   * `${medplumPatientId}:topic:${title}` or `${medplumPatientId}:chat:${index}`, so the loader can
   * resolve child CarePlans / Communications by identifier within a single transaction bundle.
   */
  SYNTHETIC_DATA_SOURCE: 'https://liza.health/fhir/synthetic-data-source',
  /**
   * ScriptSure business unit id, carried on a professional-corporation `Organization` (the P.C. a
   * practice's `partOf` points at). Together with SCRIPTSURE_PRACTICE_ID this is how Medplum's
   * ScriptSure bots resolve which SS tenant a prescriber writes under — walking
   * practice (practice-id) → partOf → P.C. (business-unit-id). This replaced the project-wide
   * `SCRIPTSURE_PRACTICE_ID` *secret* (distinct from the identifier system below, which is the
   * replacement and is here to stay). Values are SS-portal-assigned and differ per environment
   * (spa sandbox vs ppa production). See
   * docs/design-decisions/2026-07-17-practices-and-professional-corporations.md.
   */
  SCRIPTSURE_BUSINESS_UNIT_ID: 'https://scriptsure.com/business-unit-id',
  /** ScriptSure practice id, carried on a practice `Organization` — see SCRIPTSURE_BUSINESS_UNIT_ID. */
  SCRIPTSURE_PRACTICE_ID: 'https://scriptsure.com/practice-id',
  /**
   * Stable key for a standard `HealthcareService` template (e.g. `video-call`, `in-person-visit`),
   * shared across every organization that provisions one. medplum-config's deployer matches on this
   * identifier together with the owning `Organization` (via the `organization` search parameter,
   * i.e. `HealthcareService.providedBy`) so each org gets its own copy of the resource rather than
   * one shared across orgs, while still upserting instead of duplicating on redeploy.
   */
  HEALTHCARE_SERVICE_TEMPLATE_ID: 'https://liza.health/fhir/identifier/healthcare-service-template-id',
  /**
   * Marks the research conversation opened by the automatic care-plan bootstrap that runs after a
   * patient's external records are imported. The value is the Medplum `Patient` id, so the automatic
   * bootstrap happens at most once per patient for all time: the conversation is created through
   * `If-None-Exist` on this identifier, and a later import finds the existing thread and stops.
   *
   * Keyed on the patient rather than the ingestion that triggered it, deliberately. Under
   * ingestion-row keying a provider who rejected every suggestion left the care plan topic-less with
   * no pending suggestions — passing the business gates again — so the next import re-proposed the
   * same plan indefinitely. A provider who wants another draft asks for one in chat.
   *
   * A second entry under the same system carries `attempt:<uuid>`, which is how a caller tells its
   * own create from a concurrent one — see `metriportCarePlanBootstrap`.
   */
  CARE_PLAN_BOOTSTRAP: 'https://liza.health/fhir/identifier/care-plan-bootstrap',
  /**
   * Provenance stamp on a `Coverage` created in Liza (care-team or patient self-service), as opposed
   * to one imported from an external record. Value is a unique id per policy so a patient can have
   * many.
   *
   * Written on create and left untouched on update. Nothing reads it yet — list/edit include imported
   * Coverages too (`insuranceFieldsFromCoverage` ignores provenance). Kept so a later filter can tell
   * Liza-authored policies from imported ones without a data backfill.
   */
  COVERAGE_SOURCE: 'https://liza.health/fhir/identifier/coverage-source',
  /**
   * Stedi healthcare network payer id on shared `Organization` resources (`type=ins`). Value is the
   * CSV `PrimaryPayerId` / `StediId` (e.g. `60054` for Aetna). Required by Medplum's Stedi
   * eligibility flow; also the idempotency key for the payer catalog import.
   *
   * Vocabulary note: this codebase and Stedi / X12 / Medplum docs say *payer*. FHIR `Coverage` still
   * spells the field `payor` — leave that resource property as-is.
   */
  STEDI_HEALTHCARE_NETWORK: 'https://www.stedi.com/healthcare/network',
  /**
   * `Task.groupIdentifier` on the care-team tasks auto-escalation raises from a chat conversation. The
   * value is the conversation's Medplum `Communication` id. `Task.focus` holds one message and the
   * further messages a task collects live in unsearchable `input` entries, so this is what lets one
   * indexed search (`group-identifier`) find the conversation's open escalation task to link the next
   * message onto, and find every task the conversation raised when the thread marks its messages.
   * A group identifier rather than an identifier because several tasks legitimately share it — the
   * completed one and the open one that followed it.
   */
  ESCALATION_CONVERSATION: 'https://liza.health/fhir/identifier/escalation-conversation',
  /**
   * `Task.identifier` on a care-team task a clinician asked Liza for in chat. The value is the
   * Medplum `Communication` id of the message that asked, so `identifier=<system>|` finds every
   * chat-requested task and `identifier=<system>|<messageId>` finds the one a given request produced.
   *
   * A plain identifier rather than a `groupIdentifier`: one request makes one task, unlike the
   * escalation group several tasks legitimately share. Deliberately *not* the escalation group —
   * stamping that would make `raiseOrJoinEscalationTask` treat a requested task as the conversation's
   * open escalation task and link later flagged messages onto it.
   *
   * The positive marker the task data-integrity decision asks of every new Task model: the shape
   * alone (`intent: 'order'`, care-team owner, `focus` → a Communication) is also an escalation
   * task's shape, and only this tells the two apart.
   */
  LIZA_CHAT_TASK: 'https://liza.health/fhir/identifier/liza-chat-task',
  /**
   * Marks the research thread carrying an encounter's care-plan suggestions. The value is the
   * Medplum `Encounter` id, so an encounter has at most one such thread: it is created through
   * `If-None-Exist` on this identifier, which makes two providers clicking at once converge on one
   * thread rather than racing a read-then-write.
   *
   * This is also how the thread is *found* — `Communication.about` carries the encounter for
   * rendering but has no SearchParameter in R4 or Medplum's extension set, so it cannot be searched.
   * Unlike `listConversations`, a lookup on this identifier is status-agnostic, so a thread the
   * practitioner archived still counts as generated.
   */
  ENCOUNTER_SUGGESTIONS_THREAD: 'https://liza.health/fhir/identifier/encounter-suggestions-thread',
  /**
   * Marks the LLM reply one suggestions run writes into that thread. The value is the job id, so the
   * worker's create is idempotent under SQS redelivery while a retry — a new job — still gets its
   * own reply, leaving the failed one in the thread as history.
   */
  ENCOUNTER_SUGGESTIONS_REPLY: 'https://liza.health/fhir/identifier/encounter-suggestions-reply',
  /**
   * `Task.groupIdentifier` on the suggested-action proposal Tasks one conversation's runs produced.
   * The value is the conversation's Medplum `Communication` id.
   *
   * The searchable half of the model: a proposal's `focus` is one message `Communication`, and
   * nothing indexed leads from there to the conversation (`Communication.part-of` is not reachable
   * from a `Task` search, and `Communication.about` has no SearchParameter). So this is what lets one
   * `group-identifier` query find every suggestion a thread is carrying.
   *
   * A group identifier rather than an identifier because many Tasks legitimately share it — every
   * suggestion from every run in the thread.
   */
  SUGGESTED_ACTION_CONVERSATION: 'https://liza.health/fhir/identifier/suggested-action-conversation',
  /**
   * `Task.identifier` on the care-team task that reminds a practitioner to review the suggested
   * actions one message proposed. The value is that message's `Communication` id — one review task
   * per proposing message, which is what the sweep's conditional create and "already raised" check
   * both key on.
   */
  SUGGESTED_ACTION_REVIEW_MESSAGE: 'https://liza.health/fhir/identifier/suggested-action-review-message',
} as const;

/**
 * Identifier systems the e-prescriber sync bots stamp on `MedicationRequest` resources.
 * Self-reported meds get a `*_SELF_REPORTED_MEDICATION` id when synced for DDI checks; prescriptions
 * written back from the vendor carry a `*_PRESCRIPTION` id. The vendors never share a value across
 * these systems, so they cannot be used to link a self-reported med to its prescription — that
 * grouping is Liza's own logic (see medication-prescription-deduplication.md).
 */
export const VENDOR_MEDICATION_IDENTIFIER_SYSTEMS = {
  SCRIPTSURE_SELF_REPORTED_MEDICATION: 'https://scriptsure.com/medication-id',
  SCRIPTSURE_PRESCRIPTION: 'https://scriptsure.com/prescription-id',
  /** Stamped on a freshly-created order draft while it's queued in ScriptSure but not yet signed.
   * Replaced by `SCRIPTSURE_PRESCRIPTION` once the prescriber signs (via the webhook). Its presence
   * (with no `SCRIPTSURE_PRESCRIPTION`) marks an in-progress, resumable prescription. */
  SCRIPTSURE_PENDING_ORDER: 'https://scriptsure.com/pending-order-id',
  /** Stamped by `$checkout-medications` on a draft staged into ScriptSure's MedCart, alongside
   * `pending-order-status: in-cart`. Its presence marks a cart line the vendor already knows about,
   * so removing it locally must also tell ScriptSure — an unstaged line has no vendor record at all.
   *
   * Confirmed against a real staged line: checkout writes this system with a uuid value. Medplum's
   * docs name the identifier but never spell out the system URL. */
  SCRIPTSURE_MEDCART_RX: 'https://scriptsure.com/medcart-rx-id',
} as const;

/**
 * Coding and identifier systems ScriptSure's webhook stamps on the `Task`s it writes for pharmacy
 * messages — refill requests (`RxRenewalRequest`), new-prescription transmission errors (`NewRx`) and
 * friends. Distinct from the `MedicationRequest` sync systems above: these identify the *message*
 * awaiting a prescriber, not a prescription.
 *
 * `MESSAGE_TYPE` on `Task.code.coding[]` is what marks a Task as vendor-authored pharmacy work, and
 * so what gates the launch affordance (`isPharmacyMessageTask`). Our own task conventions do not
 * apply to these: they carry no `liza-task-category` input and no `owner-type` extension, since the
 * vendor knows nothing of either.
 */
export const SCRIPTSURE_PHARMACY_MESSAGE_SYSTEMS = {
  /** `Task.code.coding[].system`; codes are vendor message types, e.g. `RxRenewalRequest`, `NewRx`. */
  MESSAGE_TYPE: 'https://scriptsure.com/message-type',
  /** `Task.businessStatus.coding[].system`, e.g. `Pending`, `Error`. */
  MESSAGE_STATUS: 'https://scriptsure.com/message-status',
  /** `Task.identifier[]` — the vendor's message id (SureScripts `messageId`). Not assumed unique
   * across Tasks: resolve a message by Task id rather than reverse-looking-up on this. */
  MESSAGE_ID: 'https://scriptsure.com/message-id',
  /** `Task.identifier[]` — the vendor's request id, alongside `MESSAGE_ID`. */
  REQUEST_ID: 'https://scriptsure.com/request-id',
} as const;

/**
 * Keys on a pharmacy-message `Task.input[]`, used verbatim as `input[].type.text`.
 *
 * The vendor writes these as bare `text` with **no coding system**, so the `TASK_INPUT_KEYS_SYSTEM`
 * helpers in `taskUtils.ts` (`getTaskInstructions` and friends) will not find them — read them by
 * `type.text` instead.
 */
export const SCRIPTSURE_PHARMACY_MESSAGE_INPUT_KEYS = {
  MESSAGE_STATUS: 'messageStatus',
  DRUG_NAME: 'drugName',
  QUANTITY: 'quantity',
  INSTRUCTION: 'instruction',
  NCPDP_ID: 'ncpdpId',
  PHARMACY: 'pharmacy',
} as const;

/**
 * ScriptSure's drug-catalog identifier, carried on `Medication` resources returned by the
 * `$drug-search` operation. A "routed med" is a drug at the name level (e.g. "metformin"); a search
 * by `term` returns these, and a follow-up search by `routedMedId` returns that drug's dispensable
 * formulations (which additionally carry RxNorm/NDC). Distinct from the per-`MedicationRequest` sync
 * identifiers above — this identifies a catalog entry, not a prescription.
 */
export const SCRIPTSURE_ROUTED_MED_ID_SYSTEM = 'https://scriptsure.com/routed-med-id';

/**
 * ScriptSure's formulation key, carried on `$drug-search` `Medication` resources alongside the
 * routed-med-id. Where a routed-med-id names the drug, a GCN sequence number keys a marketed
 * formulation of it.
 *
 * It is the handle for drugs the drill-down cannot list: devices, supplies, OTC and topical
 * products have no dose-level rows, so a `routedMedId` search returns nothing and the vendor
 * resolves the formulation from this key instead (`gcnSeqnos` on `$drug-search`, `gcnSeqno` on
 * `$order-medication`).
 */
export const SCRIPTSURE_GCN_SEQNO_SYSTEM = 'https://scriptsure.com/gcn-seqno';

/** Extensions ScriptSure stamps on a `Medication` returned by `$drug-search` (drug-name search). */
export const SCRIPTSURE_DRUG_SEARCH_EXTENSIONS = {
  /** `"1"` = brand, `"2"` = generic. */
  NAME_TYPE: 'https://scriptsure.com/name-type',
  /** The parent generic name, present on brand rows (e.g. "metformin HCl" on "Glucophage"). */
  GENERIC_NAME: 'https://scriptsure.com/generic-name',
} as const;

/** Complex extension Liza stamps on `medicationCodeableConcept` carrying the structured brand and
 * generic names behind the resolved display `text`, plus the RxNorm product code the names were
 * resolved from (so a consumer can detect names gone stale after the coding changed — e.g. a drug
 * swapped at signing in the e-prescriber). Sub-extension URLs are relative per FHIR convention;
 * build/read via the helpers in `medicationNames.ts`. */
export const MEDICATION_NAMES_EXTENSION = 'https://liza.health/fhir/extension/medication-names';

/** Complex extension Liza stamps on a plan's `medicationCodeableConcept` carrying the `$drug-search`
 * formulation row the prescriber picked — the drug name and the vendor's strength/form label as two
 * fields, its provenance, and the RxNorm code it carried as a staleness marker. Recorded so the
 * product reads the same before and after it is prescribed without parsing the vendor's string.
 * Build/read via the helpers in `scriptsureFormulation.ts`. */
export const SCRIPTSURE_FORMULATION_EXTENSION = 'https://liza.health/fhir/extension/scriptsure-formulation';

/** Coding systems for medication products on `medicationCodeableConcept.coding`. */
export const MEDICATION_CODE_SYSTEMS = {
  RXNORM: 'http://www.nlm.nih.gov/research/umls/rxnorm',
  NDC: 'http://hl7.org/fhir/sid/ndc',
} as const;

/**
 * Where a cal.com booking's video-room link is carried on the R4 `Appointment`. R5 models this as
 * `Appointment.virtualService`; Medplum is R4, which has no field for it, so it lives in an extension
 * (`valueUrl`) rather than being squeezed into `location`-style free text.
 */
export const APPOINTMENT_JOIN_URL_EXTENSION = 'https://liza.health/fhir/extension/appointment-join-url';

/**
 * Coding system for the cal.com event type a booking was made against, carried on
 * `Appointment.serviceType`. The code is the event type's slug (e.g. `30min-followup`) — the same slug
 * `<username>/<slug>` addresses when booking — so the appointment type is recoverable for reporting
 * without re-reading cal.com.
 */
export const CALCOM_EVENT_TYPE_SYSTEM = 'https://liza.health/fhir/calcom-event-type';

/**
 * Coding system for `HealthcareService.type` on the standard service templates medplum-config
 * provisions per organization (e.g. `video-call`, `in-person-visit`) — distinct from
 * `CALCOM_EVENT_TYPE_SYSTEM`, which names a cal.com event type slug rather than a service kind.
 */
export const HEALTHCARE_SERVICE_TYPE_SYSTEM = 'https://liza.health/fhir/healthcare-service-type';

/**
 * `meta.tag` system marking a `HealthcareService` as staff-only (e.g. walk-in, CHW visit) — bookable by
 * patient-admin but structurally unreadable by patients. The patient `AccessPolicy`'s `HealthcareService`
 * criteria excludes this tag (`_tag:not=`), so a staff-only service is filtered at the FHIR read layer,
 * not just hidden in the UI. The `staff-only` code is the only one defined today.
 */
export const BOOKING_VISIBILITY_SYSTEM = 'https://liza.health/fhir/booking-visibility';
export const BOOKING_VISIBILITY_STAFF_ONLY_CODE = 'staff-only';

/** VSAC "Common substances for allergy and intolerance documentation" — the catalog backing both the
 * provider-facing allergen picker and the `create_allergy`/`update_allergy` chat actions. It carries
 * drugs as RxNorm members and food/environmental substances as SNOMED, so allergen resolution can
 * prefer an RxNorm code for drug–allergy interaction checks. */
export const ALLERGEN_VALUE_SET_URL = 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113762.1.4.1186.8';

/** The NCPDP provider id system carried by pharmacy `Organization`s. ScriptSure's pharmacy directory
 * search returns transient Organizations with no Medplum `id`, identified only by their NCPDP id —
 * which is the handle `$order-medication` takes as `pharmacyNcpdpId`. */
export const NCPDP_PROVIDER_ID_SYSTEM = 'http://terminology.hl7.org/CodeSystem/NCPDPProviderIdentificationNumber';

/** SNOMED CT system URL — used for route-of-administration codings on `dosage.route`. */
export const SNOMED_SYSTEM = 'http://snomed.info/sct';

/** Well-known `value`s under {@link IDENTIFIER_SYSTEMS.DEVICE}. */
export const DEVICE_IDENTIFIERS = {
  /**
   * The Liza `Device` — the single principal every Liza-authored resource is attributed to
   * (`Task.requester` on a system-created task, `Flag.author` on an auto-escalated worklist flag).
   * Provisioned once per environment by the medplum-config deploy and resolved by identifier at
   * runtime, so this value must match what config seeds.
   */
  LIZA_AI: 'liza-ai',
} as const;

/**
 * Find the value of an identifier with the given `system` in a list of identifiers.
 * Returns `null` if no match.
 */
export const getIdentifierId = (identifiers: Identifier[], system: string): string | null =>
  identifiers.find((id) => id.system === system)?.value ?? null;

const SCRIPTSURE_IDENTIFIER_SYSTEM_PROD = 'https://ppa.scriptsure.com';
const SCRIPTSURE_IDENTIFIER_SYSTEM_STAGING = 'https://spa.scriptsure.com';

/**
 * Identifier system for a clinician's ScriptSure ID on `ProjectMembership.identifier`.
 * ScriptSure's iframe SSO reads the prescriber id from this identifier.
 *
 * Stage-scoped: production points at the `ppa` portal, non-production stages (`local`, `staging`)
 * share the `spa` staging portal.
 */
export const getScriptSureIdentifierSystem = (stage: LizaStage): string =>
  stage === 'production' ? SCRIPTSURE_IDENTIFIER_SYSTEM_PROD : SCRIPTSURE_IDENTIFIER_SYSTEM_STAGING;

export const buildScriptSureIdentifier = (stage: LizaStage, value: string): Identifier => ({
  system: getScriptSureIdentifierSystem(stage),
  value,
});

export const getScriptSureIdentifierId = (identifiers: Identifier[], stage: LizaStage): string | null =>
  getIdentifierId(identifiers, getScriptSureIdentifierSystem(stage));

/**
 * Whether a resource was written by the synthetic persona loader rather than by a real user or
 * integration — it carries {@link IDENTIFIER_SYSTEMS.SYNTHETIC_DATA_SOURCE}.
 *
 * Server-side handlers that act on a resource's *content* — raising a task, calling a model — return
 * early on one: a persona declares in its TypeScript exactly which tasks it should have, and that spec
 * is the only source of a synthetic patient's tasks. Re-deriving them on each load would make a reset
 * non-deterministic, which is the property the persona system exists for.
 *
 * The check is per *resource*, deliberately not per patient: a live message someone posts into a
 * synthetic patient's thread is written by the real app, carries no synthetic identifier, and must go
 * on being handled normally — that is how these flows get tested end to end.
 */
export const isSyntheticResource = (resource: { identifier?: Identifier[] }): boolean =>
  getIdentifierId(resource.identifier ?? [], IDENTIFIER_SYSTEMS.SYNTHETIC_DATA_SOURCE) !== null;
